/**
 * MapViewComponent
 *
 * This component renders a Google Map with markers and polylines representing trails.
 * It uses the @react-google-maps/api library to interact with the Google Maps API.
 *
 * Features:
 * - Displays markers for each trail.
 * - Highlights the polyline of the selected trail in blue.
 * - Pans the map to the selected trail's coordinates when a marker is clicked.
 *
 * Dependencies:
 * - React
 * - @react-google-maps/api
 * - trails.json (contains trail data)
 *
 * Environment Variables:
 * - REACT_APP_GOOGLE_MAPS_API_KEY: Google Maps API key
 *
 * Usage:
 * Import and include the MapViewComponent in your React application.
 */
import React, { useState, useRef, useContext, useMemo } from "react";
import { GoogleMap, LoadScript, Marker, Polyline, MarkerClusterer, InfoWindow } from "@react-google-maps/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faList, faRedo } from "@fortawesome/free-solid-svg-icons"; // Icons for reset and list view
import { mapBounds } from "../utils/";
import { setData, getData } from "../utils/storage";
import { getTrailData, fetchStateTrailsUnique } from "../utils/fetch";
import { parseTrailData } from "../utils/trails";
import { useFocusEffect } from "@react-navigation/native";
import { useNavigation } from "@react-navigation/native";
import { COLORS, STRINGS, mapRestrictions, initialMapSettings } from "../constants";
import MapMarker from "../assets/imgs/point.android.png";
import SelectedMapMarker from "../assets/imgs/pointSelected.android.png"; // Add the selected marker image
import SnackbarMessage from "../components/Snackbar/SnackbarMessage";
import LoadingIndicator from "../components/Loading/LoadingIndicator";
import { FilterContext } from "../utils/filterContext";
import { logAnalyticsEvent } from "../utils/firebase";

const mapOptions = {
  restriction: mapRestrictions,
  gestureHandling: "greedy", // Enable gesture handling
  disableDefaultUI: true, // Disable default UI controls
  zoomControl: true, // Enable zoom control
  minZoom: initialMapSettings.zoom,
  styles: [
    {
      featureType: "poi",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "poi.business",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "poi.attraction",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "poi.medical",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "transit",
      stylers: [{ visibility: "off" }],
    },
  ],
};

const MapViewComponent = () => {
  logAnalyticsEvent("screen_view", { name: "Trails-MapView" });

  const navigation = useNavigation();
  const [trailCoords, setTrailCoords] = useState([]);
  const [selectedPolyline, setSelectedPolyline] = useState(null);
  const [activeMarker, setActiveMarker] = useState(null);
  const [isFirstLoad, setIsFirstLoad] = useState(true); // State to track first load
  const mapRef = useRef(null);
  const [mapOpacity, setMapOpacity] = useState(0);
  const [snackbarVisible, setSnackbarVisible] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [toggleStatusStateTrails, setToggleStatusStateTrails] = useState(null);
  const [toggleStatusCityTrails, setToggleStatusCityTrails] = useState(null);
  const [loading, setLoading] = useState(true);

  const { filterState, setFilterState } = useContext(FilterContext);

  /**
   * Handles the event when a marker is pressed.
   * Sets the selected polyline index and pans the map to the selected trail's coordinates.
   *
   * @param {number} index - The index of the selected marker.
   */
  const handleActiveMarker = (marker, index) => {
    setActiveMarker(marker);
    setSelectedPolyline(trailCoords[index].polyline);
  };

  /**
   * Resets both selected marker and polyline when clicking outside.
   */
  const handleMapClick = () => {
    setActiveMarker(null);
    setSelectedPolyline(null);
  };

  /**
   * Resets the map view to its initial bounds.
   */
  const handleResetMap = () => {
    resetMap();
    setActiveMarker(null);
    setSelectedPolyline(null);
  };

  /**
   * Toggles the visibility of state trails.
   */
  const toggleStateTrails = async () => {
    setActiveMarker(null);
    setData("showStateTrails", (await getData("showStateTrails")) === "true" ? "false" : "true");
    loadTrailData();
  };

  /**
   * Toggles the visibility of city trails.
   */
  const toggleCityTrails = async () => {
    setActiveMarker(null);
    const showCityTrails = (await getData("showCityTrails")) === "true" ? "false" : "true";
    setData("showCityTrails", showCityTrails);
    setFilterState(showCityTrails);
    loadTrailData();
  };

  /**
   * Resets the map view to its initial bounds.
   */
  const resetMap = () => {
    if (mapRef.current) {
      mapRef.current.setCenter(initialMapSettings.center);
      mapRef.current.setZoom(initialMapSettings.zoom);
    }
  };

  /**
   * useEffect hook to load trail coordinates when the component mounts.
   * This effect runs only once because it has an empty dependency array.
   * Loads the trail data from the JSON file and stores it in local storage.
   * Fits the map bounds to the loaded trail data.
   */
  const loadTrailData = async () => {
    try {
      let showCityTrails = (await getData("showCityTrails")) ?? (setData("showCityTrails", "true"), true);
      let showStateTrails = (await getData("showStateTrails")) ?? (setData("showStateTrails", "true"), true);
      setToggleStatusStateTrails(showStateTrails);
      setToggleStatusCityTrails(showCityTrails);
      const data = await getTrailData();
      const newTrailCoords = await parseTrailData(data);
      const stateData = await fetchStateTrailsUnique();
      const newStateData = await parseTrailData(stateData);

      let trails = [];
      if (String(showCityTrails) === "true") {
        trails = trails.concat(newTrailCoords);
      }
      if (String(showStateTrails) === "true") {
        trails = trails.concat(newStateData);
      }

      setData("trailData", JSON.stringify(newTrailCoords));
      setData("stateTrailData", JSON.stringify(newStateData));

      setTrailCoords(trails);
      let showSnackbar = await getData("showSnackbar");
      if (showSnackbar === "true") {
        setSnackbarMessage(`${trails.length} results found.`);
        setSnackbarVisible(true);
        setTimeout(() => {
          setSnackbarVisible(false);
          setData("showSnackbar", "false");
        }, 4500);
      }
    } catch (error) {
      // console.error('Error loading trail data:', error);
    }
  };

  /**
   * Focus effect to load the trail data when the component is focused.
   */
  useFocusEffect(
    React.useCallback(() => {
      loadTrailData();
    }, [])
  );

  /**
   * Sets the map instance.
   *
   * @param {GoogleMap} map - The map instance.
   */
  const onLoad = async (map) => {
    mapRef.current = map;
  };

  const tilesLoaded = () => {
    if (isFirstLoad) {
      resetMap(); // Only reset map if it's the first time the map is loaded
      setIsFirstLoad(false); // Set first load to false after resetting the map
      setMapOpacity(1); // Set map opacity to 1 after resetting the map
      loadTrailData();
    }
    setLoading(false);
  };

  const renderedMarkers = useMemo(() => {
    return (clusterer) =>
      trailCoords.map((trail, index) => (
        <Marker
          key={index}
          position={{ lat: trail.lat, lng: trail.lng }}
          clusterer={clusterer}
          onClick={() => {
            handleActiveMarker(trail.attributes.OBJECTID || trail.attributes.objectid, index);
          }}
          icon={{
            url:
              activeMarker ===
              (trail.attributes.OBJECTID !== undefined ? trail.attributes.OBJECTID : trail.attributes.objectid)
                ? SelectedMapMarker
                : MapMarker, // Change icon based on active marker
            scaledSize: new window.google.maps.Size(50, 50), // Adjust size
          }}
        >
          {activeMarker ===
          (trail.attributes.OBJECTID !== undefined ? trail.attributes.OBJECTID : trail.attributes.objectid) ? (
            <InfoWindow
              onCloseClick={() => {
                setActiveMarker(null);
                setSelectedPolyline(null);
              }}
            >
              <div
                style={{
                  paddingRight: 12,
                  paddingBottom: 12,
                  paddingTop: 12,
                  cursor: "pointer",
                  fontSize: 18,
                  fontWeight: "bold",
                }}
                onClick={() => {
                  setActiveMarker(null);
                  if (typeof trail.attributes.District === "undefined") {
                    navigation.navigate("DetailsView", {
                      id: trail.attributes.OBJECTID,
                    });
                  } else {
                    navigation.navigate("StateDetailsView", {
                      id: trail.attributes.objectid,
                    });
                  }
                }}
                onKeyUp={(e) => {
                  setActiveMarker(null);
                  if (e.key === "Enter" || e.key === " ") {
                    if (typeof trail.attributes.District === "undefined") {
                      navigation.navigate("DetailsView", {
                        id: trail.attributes.OBJECTID,
                      });
                    } else {
                      navigation.navigate("StateDetailsView", {
                        id: trail.attributes.objectid,
                      });
                    }
                  }
                }}
                role="button"
                tabIndex={0}
              >
                {trail.attributes.segment_name}
              </div>
            </InfoWindow>
          ) : null}
        </Marker>
      ));
  }, [trailCoords, activeMarker, navigation]);

  const renderedPolylines = useMemo(() => {
    const polylines = [];
    trailCoords.forEach((trail, trailIndex) => {
      // Check if this is a multi-segment trail (state trails)
      const geometry = trail.geometry || { paths: [trail.polyline] };
      const paths = geometry.paths || [trail.polyline];
      
      // Render each path segment separately to avoid connecting lines
      paths.forEach((pathSegment, segmentIndex) => {
        const coordinates = Array.isArray(pathSegment[0]) && Array.isArray(pathSegment[0][0]) 
          ? pathSegment.flat() 
          : pathSegment;
          
        polylines.push(
          <Polyline
            key={`${trailIndex}-${segmentIndex}`}
            path={coordinates.map((point) => ({
              lat: point[1],
              lng: point[0],
            }))}
            options={{
              strokeColor: selectedPolyline === trail.polyline ? COLORS.green : COLORS.secondary,
              strokeOpacity: 0.8,
              strokeWeight: 8,
              icons: [
                {
                  icon: {
                    path: "M 0,-1 0,1",
                    strokeOpacity: 1,
                    scale: 2.5,
                    strokeColor: selectedPolyline === trail.polyline ? COLORS.textLight : COLORS.descriptionText,
                  },
                  offset: "0",
                  repeat: "20px",
                },
              ],
            }}
          />
        );
      });
    });
    return polylines;
  }, [trailCoords, selectedPolyline]);

  return (
    <div style={styles.mapContainer}>
      <LoadingIndicator loading={loading} />
      <SnackbarMessage visible={snackbarVisible} message={snackbarMessage} />
      <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
        <GoogleMap
          gestureHandling="greedy"
          mapContainerStyle={{
            height: "100%",
            width: "100%",
            opacity: mapOpacity,
          }}
          onLoad={(map) => {
            onLoad(map);
            map.fitBounds(mapBounds(trailCoords));
          }}
          onTilesLoaded={tilesLoaded}
          center={initialMapSettings.center}
          zoom={initialMapSettings.zoom}
          options={mapOptions}
          onClick={handleMapClick} // Reset marker and polyline when clicking the map
        >
          <MarkerClusterer>{(clusterer) => renderedMarkers(clusterer)}</MarkerClusterer>
          {renderedPolylines}
        </GoogleMap>
      </LoadScript>
      {/* Floating action buttons */}
      <div style={styles.fabContainer}>
        {/* State trails toggle button */}
        <button
          style={{
            ...styles.fab,
            ...{
              backgroundColor: toggleStatusStateTrails === "true" ? COLORS.secondary : COLORS.secondaryOff,
            },
          }}
          onClick={toggleStateTrails}
          aria-label={STRINGS.stateTrails}
        >
          {STRINGS.stateTrails}
        </button>

        {/* City trails toggle button */}
        <button
          style={{
            ...styles.fab,
            ...{
              backgroundColor: toggleStatusCityTrails === "true" ? COLORS.secondary : COLORS.secondaryOff,
            },
          }}
          onClick={toggleCityTrails}
          aria-label={STRINGS.cityTrails}
        >
          {STRINGS.cityTrails}
        </button>

        {/* Reset button */}
        <button data-testid="reset-button" style={styles.fab} onClick={handleResetMap} aria-label={STRINGS.resetMap}>
          <FontAwesomeIcon icon={faRedo} size="lg" color={COLORS.textDark} />
        </button>

        {/* List view button */}
        <button
          style={styles.fab}
          onClick={() => navigation.navigate("ListView")}
          aria-label={STRINGS.listViewTitle}
          data-testid="list-view-button"
        >
          <FontAwesomeIcon icon={faList} size="lg" color={COLORS.textDark} />
        </button>
      </div>
    </div>
  );
};

/**
 * Styles for the FABs.
 */
const isDvhSupported = window.CSS && CSS.supports("height", "100dvh");
const styles = {
  mapContainer: {
    height: isDvhSupported ? "calc(100dvh - 64px)" : "100vh",
    width: "100%",
    position: "fixed",
  },
  fabContainer: {
    position: "fixed",
    bottom: "8px",
    right: "8px",
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    zIndex: 1000,
  },
  fab: {
    width: "56px",
    height: "56px",
    borderRadius: 8,
    borderColor: COLORS.secondaryBorder,
    borderWidth: 1,
    backgroundColor: COLORS.secondary,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.3)",
    cursor: "pointer",
    fontSize: 16,
    color: COLORS.textDark,
  },
};

export default MapViewComponent;
